import { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { AnimatePresence, m } from 'framer-motion';
import {
    Button,
    CircularProgress,
    ClickAwayListener,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    MenuItem,
    Paper,
    Popper,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import Iconify from '../Iconify';
import AssignedToLead from '../AssignedToLead';
import { dispatch, useSelector } from '../../redux/store';
import { bulkArchivedOrRestore } from '../../redux/slices/lead';
import { updateError } from '../../redux/slices/user';
import { getAllTagItem } from '../../redux/slices/tagsCreate';
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
import useCustomSnackbar from '../../hooks/useCustomSnackbar';
import { CHECKLIST_ITEM_TYPE, DEPRECATED_CHECKLIST_ITEM_TYPES } from '../../constants/tasksV2';

// ── Archive reasons ───────────────────────────────────────────────────────────
const ARCHIVE_REASONS = [
    { value: 'CREDIT_CHECK_FAILED', label: 'Credit check failed' },
    { value: 'TOO_EXPENSIVE', label: 'Too expensive' },
    { value: 'CHANGED_MIND', label: 'Changed mind' },
    { value: 'DUPLICATE_ENTRY', label: 'Duplicate entry' },
    { value: 'WANT_NHS', label: 'Want NHS' },
    { value: 'CLINICALLY_NOT_SUITABLE', label: 'Clinically not suitable' },
    { value: 'PRACTICE_TOO_FAR', label: 'Practice too far' },
    { value: 'ANOTHER_PRACTICE', label: 'Another practice' },
    { value: 'NO_RESPONSE', label: 'No response' },
    { value: 'OTHER', label: 'Other' },
];

// ── Checklist bulk helpers ────────────────────────────────────────────────────

// Per-item bulk-completability check.
// SOURCE OF TRUTH: CHECKLIST_ITEM_TYPE enum (src/constants/tasksV2.js) +
// TaskChecklistField.jsx per-type switch + cell-handler payload audit.
//
// Only plain CONFIRMATION (item_type === CONFIRMATION && !action_config.requires_confirmation)
// is bulk-safe.  Reasoning:
//   - Plain CONFIRMATION: ChecklistCell sends postState(YES) with NO response_payload.
//     Bulk sends {answer:'manual'}, which is extraneous but display-neutral — ConfirmationCell
//     only maps 'yes'→'Confirmed' / 'no'→'Declined'; 'manual' falls back to null displayLabel,
//     identical to the single-path outcome.  No user choice is required.
//   - requires_confirmation CONFIRMATION: pops a Yes/No dialog; stores answer:'yes'/'no';
//     renders 'Confirmed'/'Declined'.  Bulk posting answer:'manual' records neither →
//     false confirmation.  → Skipped, surfaced in needs_data.
//   - YES_NO_TOGGLE: two-button inline YES/NO; payload { answer:'yes'|'no' }; renders the
//     saved answer directly (line 1106 TaskChecklistField.jsx).  Bulk posting answer:'manual'
//     renders the literal string "manual" → data-integrity problem.  → Skipped, needs_data.
//   - All other types: send action, user data, status side-effect, or artifact required.
//
// DEFAULT-DENY: an item not passing this check is always skipped + surfaced, so future
// template types can't silently reintroduce false-complete.
function isBulkCompletable(item) {
    return (
        item.item_type === CHECKLIST_ITEM_TYPE.CONFIRMATION &&
        !item.action_config?.requires_confirmation
    );
}

// Maps each non-completable type to a reason group that drives the P0 dialog copy.
// YES_NO_TOGGLE is in needs_data because the user's answer (yes/no) is required.
// needs_next_action is derived from DEPRECATED_CHECKLIST_ITEM_TYPES (all represent
// status-side-effect actions subsumed by LEAD_NEXT_ACTION) plus LEAD_NEXT_ACTION itself,
// so future deprecations added to that Set are automatically covered here.
// Types not matching any group fall back to 'needs_data' (conservative).
const SKIP_GROUP = {
    needs_send: new Set([
        CHECKLIST_ITEM_TYPE.EMAIL_SENT,
        CHECKLIST_ITEM_TYPE.WHATSAPP_SENT,
        CHECKLIST_ITEM_TYPE.SMS_SENT,
        CHECKLIST_ITEM_TYPE.SEND_CONSENT_FORM,
        CHECKLIST_ITEM_TYPE.SEND_REVIEW_REQUEST,
        CHECKLIST_ITEM_TYPE.SEND_PAYMENT_LINK,
        CHECKLIST_ITEM_TYPE.SEND_TREATMENT_PLAN,
        CHECKLIST_ITEM_TYPE.LOG_PHONE_CALL,
    ]),
    needs_data: new Set([
        CHECKLIST_ITEM_TYPE.YES_NO_TOGGLE,  // user must choose yes or no — answer matters
        CHECKLIST_ITEM_TYPE.FREE_TEXT,
        CHECKLIST_ITEM_TYPE.MULTIPLE_CHOICE,
        CHECKLIST_ITEM_TYPE.ADD_NOTE,
        CHECKLIST_ITEM_TYPE.CURRENCY_AMOUNT,
        CHECKLIST_ITEM_TYPE.PAYMENT_METHOD,
        CHECKLIST_ITEM_TYPE.STAFF_PICKER,
        CHECKLIST_ITEM_TYPE.DATE_FIELD,
    ]),
    // All deprecated types (Wave 1 + 2 status-side-effect items) plus the active LNA.
    needs_next_action: new Set([
        ...DEPRECATED_CHECKLIST_ITEM_TYPES,
        CHECKLIST_ITEM_TYPE.LEAD_NEXT_ACTION,
    ]),
    needs_artifact: new Set([
        CHECKLIST_ITEM_TYPE.UPLOAD_DOCUMENT,
        CHECKLIST_ITEM_TYPE.LINKED_CHECKLIST,
        CHECKLIST_ITEM_TYPE.SCHEDULE_REMINDER,
        CHECKLIST_ITEM_TYPE.BOOK_APPOINTMENT,
    ]),
};

function skipGroupFor(itemType) {
    const entry = Object.entries(SKIP_GROUP).find(([, s]) => s.has(itemType));
    return entry ? entry[0] : 'needs_data'; // conservative fallback for unrecognised types
}

// callApi never throws — it returns { ok, status, data, error }.
// Helper to surface a readable message from a failed response.
function apiErrMsg(res) {
    const body = res?.data;
    if (typeof body === 'string') return body;
    if (body?.detail) return body.detail;
    if (body?.message) return body.message;
    return `HTTP ${res?.status || 'error'}`;
}

async function fetchTaskItems(practiceId, leadIds) {
    // task-template-instances and /tasks/ list both omit checklist_items.
    // Use /tasks/?lead= to enumerate tasks, then detail-fetch each for checklist_items.
    const perLead = await Promise.all(
        leadIds.map(async (leadId) => {
            const listRes = await callApi(
                `/api/v1/practices/${practiceId}/tasks/?lead=${leadId}&limit=50`,
                'GET'
            );
            if (!listRes.ok) throw new Error(`Failed to fetch tasks for lead ${leadId}: ${apiErrMsg(listRes)}`);
            const taskStubs = listRes.data?.results || [];
            const tasks = await Promise.all(
                taskStubs.map(async (stub) => {
                    const detail = await callApi(
                        `/api/v1/practices/${practiceId}/tasks/${stub.id}/`,
                        'GET'
                    );
                    if (!detail.ok) throw new Error(`Failed to fetch task ${stub.id}: ${apiErrMsg(detail)}`);
                    return detail.data;
                })
            );
            return { leadId, tasks };
        })
    );
    return perLead;
}

async function bulkChecklistAction(practiceId, leadIds, endpoint, body) {
    const leadTasks = await fetchTaskItems(practiceId, leadIds);
    // Per-lead counts so callers can produce truthful toasts:
    //   - leadsWithItems: how many selected leads contributed at least one
    //     actionable checklist item (powers "N tasks completed across M leads")
    //   - total === 0 means no selected lead had any actionable items, which
    //     must surface as an info toast, NOT a success toast.
    //
    // When isBulkYesComplete, only plain CONFIRMATION items
    // (item_type === CONFIRMATION && !requires_confirmation) are posted.
    // Everything else is skipped and grouped by reason so the P0 dialog
    // in NewLead.jsx can show group-specific copy explaining what the user must do
    // manually.  This prevents false audit-trail entries for items that require a send
    // action, user-provided data, a status side-effect, or an artifact.
    // Bulk SKIPPED/PAUSED are unaffected — the allowlist only gates YES-complete.
    const isBulkYesComplete = endpoint === 'complete' && body?.completion_state === 'YES';
    let leadsWithItems = 0;
    let totalSkipped = 0;
    // Sets of lead IDs keyed by skip reason, populated only when isBulkYesComplete.
    const skippedLeadsByGroup = {
        needs_send:        new Set(),
        needs_data:        new Set(),
        needs_next_action: new Set(),
        needs_artifact:    new Set(),
    };
    // Per-lead metadata collected while building the flat calls array so we can gate
    // completedLeadIds AFTER Promise.allSettled. A lead qualifies for animated removal
    // only when ALL its actionable items were successfully POSTed AND no non-completable
    // item was skipped (a skipped item leaves the item PENDING, so the lead is NOT
    // fully terminal). This mirrors makeOnChecklistComplete's allTerminal guard.
    const perLeadMeta = []; // [{ leadId, callCount, hadSkipped }]
    // Parallel to calls: one entry per call, used post-settle to build the named
    // failure list for P1 UX (shows "Couldn't complete 'Title'" instead of a bare count).
    const itemMeta = []; // [{ leadId, itemTitle }]
    const calls = [];
    leadTasks.forEach(({ leadId, tasks }) => {
        let hadSkipped = false;
        let callCount = 0;
        tasks.forEach((task) => {
            (task.checklist_items || []).forEach((item) => {
                if (!['PENDING', 'PAUSED', 'IN_PROGRESS'].includes(item.completion_state)) return;
                if (isBulkYesComplete && !isBulkCompletable(item)) {
                    const grp = skipGroupFor(item.item_type);
                    skippedLeadsByGroup[grp].add(String(leadId));
                    totalSkipped += 1;
                    hadSkipped = true;
                    return;
                }
                calls.push(
                    callApi(
                        `/api/v1/practices/${practiceId}/tasks/${task.id}/checklist/${item.id}/${endpoint}/`,
                        'POST',
                        body
                    ).then((res) => {
                        if (!res.ok) throw new Error(apiErrMsg(res));
                        return res;
                    })
                );
                itemMeta.push({ leadId, itemTitle: item.title || 'item' });
                callCount += 1;
            });
        });
        if (callCount > 0) leadsWithItems += 1;
        perLeadMeta.push({ leadId, callCount, hadSkipped });
    });
    const results = await Promise.allSettled(calls);
    const failures = results.filter((r) => r.status === 'rejected');
    // P1: named failure list — pairs each rejected result with its item metadata so
    // the caller can surface "Couldn't complete 'Title'" instead of a bare error count.
    // itemMeta and calls are built in lock-step so indices always align.
    const failedItems = results
        .map((r, idx) => (r.status === 'rejected' ? itemMeta[idx] : null))
        .filter(Boolean);
    // Build completedLeadIds post-settle: slice each lead's result window and confirm
    // all calls succeeded AND no item was skipped (a skipped item leaves the lead
    // non-terminal so animated removal would remove a lead that still has work to do).
    const completedLeadIds = [];
    // P0: skip groups surfaced as a persistent dialog by NewLead.jsx (handleItemsSkipped).
    const skippedGroups = {
        needs_send:        [...skippedLeadsByGroup.needs_send],
        needs_data:        [...skippedLeadsByGroup.needs_data],
        needs_next_action: [...skippedLeadsByGroup.needs_next_action],
        needs_artifact:    [...skippedLeadsByGroup.needs_artifact],
    };
    let callIdx = 0;
    perLeadMeta.forEach(({ leadId, callCount, hadSkipped }) => {
        if (callCount > 0) {
            const leadFailed = results.slice(callIdx, callIdx + callCount).some((r) => r.status === 'rejected');
            if (!hadSkipped && !leadFailed) {
                completedLeadIds.push(String(leadId));
            }
        }
        callIdx += callCount;
    });
    return {
        total: results.length,
        failed: failures.length,
        leadsWithItems,
        totalSkipped,
        completedLeadIds,
        skippedGroups,
        failedItems,
        firstError: failures[0]?.reason?.message,
    };
}

async function bulkReopenChecklist(practiceId, leadIds) {
    const leadTasks = await fetchTaskItems(practiceId, leadIds);
    let leadsWithItems = 0;
    const calls = leadTasks.flatMap(({ tasks }) => {
        const leadCalls = tasks.flatMap((task) =>
            (task.checklist_items || [])
                .filter((item) => ['YES', 'SKIPPED'].includes(item.completion_state))
                .map((item) =>
                    callApi(
                        `/api/v1/practices/${practiceId}/tasks/${task.id}/checklist/${item.id}/reopen/`,
                        'POST',
                        {}
                    ).then((res) => {
                        if (!res.ok) throw new Error(apiErrMsg(res));
                        return res;
                    })
                )
        );
        if (leadCalls.length > 0) leadsWithItems += 1;
        return leadCalls;
    });
    const results = await Promise.allSettled(calls);
    const failures = results.filter((r) => r.status === 'rejected');
    // See bulkChecklistAction — `total` counts item-level POSTs, not leads.
    return {
        total: results.length,
        failed: failures.length,
        leadsWithItems,
        firstError: failures[0]?.reason?.message,
    };
}

// ── Lead group helpers ────────────────────────────────────────────────────────

async function bulkAssignLeads(practiceId, leadIds, assigneeIds) {
    const res = await callApi(
        `/api/v1/practices/${practiceId}/leads/bulk_update/?is_archived_included=true`,
        'POST',
        { ids: leadIds.map(Number), field: 'assignees', value: assigneeIds }
    );
    if (res?.status !== 200) throw new Error('Assign failed');
}

async function bulkTagLeads(practiceId, leadIds, tagIds, mode) {
    const endpoint = mode === 'remove' ? 'untag' : 'tag';
    const results = await Promise.allSettled(
        leadIds.map((id) =>
            callApi(
                `/api/v1/practices/${practiceId}/leads/${id}/${endpoint}/`,
                'POST',
                { tag_ids: tagIds }
            ).then((res) => {
                if (!res.ok) throw new Error(apiErrMsg(res));
                return res;
            })
        )
    );
    const failures = results.filter((r) => r.status === 'rejected');
    // bulkTagLeads issues one call per lead, so `total` here equals lead count —
    // but we still expose `total` so the snackbar consumer can use a single
    // consistent format across all bulk helpers.
    return {
        total: results.length,
        failed: failures.length,
        firstError: failures[0]?.reason?.message,
    };
}

// ── Reusable confirm dialog ───────────────────────────────────────────────────

function ConfirmDialog({ open, onClose, title, body, onConfirm, loading, confirmLabel = 'Confirm', confirmColor = 'primary' }) {
    return (
        <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
            <DialogTitle sx={{ fontSize: '1rem', pb: 1 }}>{title}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary">{body}</Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button onClick={onClose} variant="outlined" disabled={loading} sx={{ flex: 1, borderRadius: 1.5, textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button onClick={onConfirm} variant="contained" color={confirmColor} disabled={loading} sx={{ flex: 1, borderRadius: 1.5, textTransform: 'none' }}>
                    {loading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
ConfirmDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    body: PropTypes.node.isRequired,
    onConfirm: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    confirmLabel: PropTypes.string,
    confirmColor: PropTypes.string,
};
ConfirmDialog.defaultProps = { loading: false, confirmLabel: 'Confirm', confirmColor: 'primary' };

// ── ActionButton — icon+label on desktop, icon-only on mobile ────────────────

const BTN_COLOR = 'rgba(255,255,255,0.86)';
const BTN_HOVER_BG = 'rgba(255,255,255,0.08)';
const BTN_ACTIVE_BG = 'rgba(255,255,255,0.14)';

function ActionButton({ icon, label, onClick, loading, accentColor, btnRef }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const color = accentColor || BTN_COLOR;

    const btn = (
        <Button
            ref={btnRef}
            variant="text"
            size="small"
            onClick={onClick}
            disabled={loading}
            startIcon={
                loading
                    ? <CircularProgress size={14} sx={{ color }} />
                    : <Iconify icon={icon} width={16} sx={{ color }} />
            }
            sx={{
                color,
                textTransform: 'none',
                minWidth: 'auto',
                px: 1.5,
                '&:hover': { bgcolor: BTN_HOVER_BG },
                '&:active': { bgcolor: BTN_ACTIVE_BG },
            }}
        >
            {!isMobile && label}
        </Button>
    );

    return isMobile ? <Tooltip title={label} placement="top">{btn}</Tooltip> : btn;
}
ActionButton.propTypes = {
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    accentColor: PropTypes.string,
    btnRef: PropTypes.any,
};
ActionButton.defaultProps = { loading: false, accentColor: null, btnRef: null };

// ── FloatingBulkActionBar ─────────────────────────────────────────────────────

function FloatingBulkActionBar({ practiceId, onClearSelection, onRefresh, onBulkComplete, onOpenBulkReminder, onItemsSkipped }) {
    const multiLead = useSelector((state) => state.lead.multiLead);
    const allTags = useSelector((state) => state.tagsCreate?.getAllTag?.results || []);
    const { enqueueSnackbar } = useCustomSnackbar();

    const count = multiLead?.leadId?.length || 0;
    const leadIds = multiLead?.leadId || [];
    const n = (word) => `${count} ${word}${count !== 1 ? 's' : ''}`;
    // Risk 3 fix: hide checklist bulk actions on V1 practices.
    // On V1, the per-checklist endpoints return 403, producing a silent
    // error toast; on V2 with zero tasks selected they return success with
    // no PATCH calls — both produce misleading UX. Gating the buttons
    // entirely on V2 access is the cleanest fix.
    const checklistActionsEnabled = hasFeatureAccess('tasks-v2', practiceId);
    const tasksLabel = (items) => `${items} task${items !== 1 ? 's' : ''}`;
    const leadsWord = (leads) => `${leads} lead${leads !== 1 ? 's' : ''}`;

    const afterAction = useCallback((msg) => {
        onClearSelection();
        if (onRefresh) onRefresh();
        enqueueSnackbar(msg, { variant: 'success' });
    }, [onClearSelection, onRefresh, enqueueSnackbar]);

    // ── Mark all done ─────────────────────────────────────────────
    const [doneOpen, setDoneOpen] = useState(false);
    const [doneLoading, setDoneLoading] = useState(false);

    const handleDoneConfirm = async () => {
        setDoneLoading(true);
        try {
            const {
                total,
                failed,
                leadsWithItems,
                totalSkipped,
                completedLeadIds,
                skippedGroups,
                failedItems,
            } = await bulkChecklistAction(
                practiceId, leadIds, 'complete',
                { completion_state: 'YES', response_payload: { answer: 'manual' } }
            );
            setDoneOpen(false);
            // P0: surface persistent skip dialog in the parent (NewLead.jsx) for ALL
            // non-completable item types — not just LNA.  Called for any outcome that
            // had skipped items — success, partial failure, or zero-other-items — before
            // the snackbar fires so the modal is already visible when the bar disappears.
            const hasSkipped = Object.values(skippedGroups).some((ids) => ids.length > 0);
            if (hasSkipped && onItemsSkipped) {
                onItemsSkipped(skippedGroups);
            }
            const skipNote = totalSkipped > 0
                ? ` (${totalSkipped} item${totalSkipped === 1 ? '' : 's'} need manual action — see prompt above)`
                : '';
            if (total === 0 && totalSkipped === 0) {
                // Risk 3 fix: no checklist items existed on any selected lead.
                // Was previously showing "${count} leads marked complete" — false success.
                enqueueSnackbar('No checklist tasks found on selected leads.', { variant: 'info' });
                onClearSelection();
            } else if (total === 0 && totalSkipped > 0) {
                // All items were non-completable types — nothing was bulk-posted.
                // The persistent P0 dialog already fired above; snackbar is secondary.
                enqueueSnackbar(`No items completed.${skipNote}`, { variant: 'info' });
                onClearSelection();
            } else if (failed === 0) {
                // All items succeeded: drive animation + counter refresh via onBulkComplete
                // instead of the bare onRefresh() call that crashed (Cannot destructure
                // 'limit' of undefined — updateLeadPage called with no args).
                // onBulkComplete handles: refreshCounters (badge update) + per-lead exit
                // animation + optimistic row removal + 6.5 s reconcile refetch.
                onClearSelection();
                enqueueSnackbar(`${tasksLabel(total)} completed across ${leadsWord(leadsWithItems)}.${skipNote}`, { variant: 'success' });
                if (onBulkComplete) onBulkComplete(completedLeadIds);
            } else {
                // P1: name the failing items instead of showing a bare count.
                // Slice to 2 so the toast stays readable; report the remainder as "+N more".
                // failedItems and calls are built in lock-step so failedItems.length === failed
                // always — no fallback branch needed.
                const namedFailures = failedItems.slice(0, 2).map((f) => `"${f.itemTitle}"`).join(', ');
                const moreCount = failedItems.length > 2 ? ` (+${failedItems.length - 2} more)` : '';
                const failDetail = `Couldn't complete: ${namedFailures}${moreCount}.`;
                enqueueSnackbar(
                    `Complete — ${total - failed} of ${total} items succeeded. ${failDetail}${skipNote}`,
                    { variant: 'warning' }
                );
                onClearSelection();
                if (onRefresh) onRefresh();
            }
        } catch (err) {
            // Fix C: friendlier error when fetchTaskItems can't load tasks
            // (403 from V1 slipping through, network error, etc).
            const msg = /^Failed to fetch task/i.test(err?.message || '')
                ? "Couldn't load tasks for selected leads. Please retry."
                : `Bulk complete failed — ${err?.message || 'please try again.'}`;
            dispatch(updateError({ success: false, message: msg }));
        } finally {
            setDoneLoading(false);
        }
    };

    // ── Skip all ──────────────────────────────────────────────────
    const [skipOpen, setSkipOpen] = useState(false);
    const [skipLoading, setSkipLoading] = useState(false);

    const handleSkipConfirm = async () => {
        setSkipLoading(true);
        try {
            const { total, failed, leadsWithItems, firstError } = await bulkChecklistAction(
                practiceId, leadIds, 'complete',
                { completion_state: 'SKIPPED', response_payload: { answer: 'manual' } }
            );
            setSkipOpen(false);
            if (total === 0) {
                enqueueSnackbar('No checklist tasks found on selected leads.', { variant: 'info' });
                onClearSelection();
            } else if (failed === 0) {
                afterAction(`${tasksLabel(total)} skipped across ${leadsWord(leadsWithItems)}.`);
            } else {
                enqueueSnackbar(`Skip — ${total - failed} of ${total} items succeeded, ${failed} failed${firstError ? `: ${firstError}` : ''}`, { variant: 'warning' });
                onClearSelection();
                if (onRefresh) onRefresh();
            }
        } catch (err) {
            const msg = /^Failed to fetch task/i.test(err?.message || '')
                ? "Couldn't load tasks for selected leads. Please retry."
                : `Bulk skip failed — ${err?.message || 'please try again.'}`;
            dispatch(updateError({ success: false, message: msg }));
        } finally {
            setSkipLoading(false);
        }
    };

    // ── Pause all ─────────────────────────────────────────────────
    const [pauseOpen, setPauseOpen] = useState(false);
    const [pauseDate, setPauseDate] = useState('');
    const [pauseLoading, setPauseLoading] = useState(false);

    const handlePauseConfirm = async () => {
        if (!pauseDate) return;
        setPauseLoading(true);
        try {
            const { total, failed, leadsWithItems, firstError } = await bulkChecklistAction(
                practiceId, leadIds, 'pause',
                { paused_until: new Date(pauseDate).toISOString() }
            );
            setPauseOpen(false);
            const dateLabel = new Date(pauseDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
            setPauseDate('');
            if (total === 0) {
                enqueueSnackbar('No checklist tasks found on selected leads.', { variant: 'info' });
                onClearSelection();
            } else if (failed === 0) {
                afterAction(`${tasksLabel(total)} paused until ${dateLabel} across ${leadsWord(leadsWithItems)}.`);
            } else {
                enqueueSnackbar(`Pause — ${total - failed} of ${total} items succeeded, ${failed} failed${firstError ? `: ${firstError}` : ''}`, { variant: 'warning' });
                onClearSelection();
                if (onRefresh) onRefresh();
            }
        } catch (err) {
            const msg = /^Failed to fetch task/i.test(err?.message || '')
                ? "Couldn't load tasks for selected leads. Please retry."
                : `Bulk pause failed — ${err?.message || 'please try again.'}`;
            dispatch(updateError({ success: false, message: msg }));
        } finally {
            setPauseLoading(false);
        }
    };

    // ── Re-open all ───────────────────────────────────────────────
    const [reopenOpen, setReopenOpen] = useState(false);
    const [reopenLoading, setReopenLoading] = useState(false);

    const handleReopenConfirm = async () => {
        setReopenLoading(true);
        try {
            const { total, failed, leadsWithItems, firstError } = await bulkReopenChecklist(practiceId, leadIds);
            setReopenOpen(false);
            if (total === 0) {
                enqueueSnackbar('No completed or skipped checklist tasks found on selected leads.', { variant: 'info' });
                onClearSelection();
            } else if (failed === 0) {
                afterAction(`${tasksLabel(total)} reopened across ${leadsWord(leadsWithItems)}.`);
            } else {
                enqueueSnackbar(`Reopen — ${total - failed} of ${total} items succeeded, ${failed} failed${firstError ? `: ${firstError}` : ''}`, { variant: 'warning' });
                onClearSelection();
                if (onRefresh) onRefresh();
            }
        } catch (err) {
            const msg = /^Failed to fetch task/i.test(err?.message || '')
                ? "Couldn't load tasks for selected leads. Please retry."
                : `Bulk reopen failed — ${err?.message || 'please try again.'}`;
            dispatch(updateError({ success: false, message: msg }));
        } finally {
            setReopenLoading(false);
        }
    };

    // ── Assign ────────────────────────────────────────────────────
    const assignBtnRef = useRef(null);
    const [assignOpen, setAssignOpen] = useState(false);
    const [assignees, setAssignees] = useState([]);
    const [assignLoading, setAssignLoading] = useState(false);

    const handleAssignApply = async () => {
        if (assignees.length === 0) return;
        setAssignLoading(true);
        try {
            await bulkAssignLeads(practiceId, leadIds, assignees.map((a) => a.id));
            setAssignOpen(false);
            setAssignees([]);
            afterAction(`${n('lead')} assigned to ${assignees.map((a) => a._name).join(', ')}`);
        } catch {
            dispatch(updateError({ success: false, message: 'Bulk assign failed — please try again.' }));
        } finally {
            setAssignLoading(false);
        }
    };

    // ── Tag ───────────────────────────────────────────────────────
    const tagBtnRef = useRef(null);
    const [tagOpen, setTagOpen] = useState(false);
    const [tagMode, setTagMode] = useState('add');
    const [selectedTagIds, setSelectedTagIds] = useState([]);
    const [tagLoading, setTagLoading] = useState(false);

    // Fetch tag list when popover opens
    useEffect(() => {
        if (tagOpen && allTags.length === 0) {
            dispatch(getAllTagItem(practiceId, 100, 0));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tagOpen, practiceId]);

    const handleTagToggle = (tagId) => {
        setSelectedTagIds((prev) =>
            prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
        );
    };

    const handleTagApply = async () => {
        if (selectedTagIds.length === 0) return;
        setTagLoading(true);
        try {
            const { total, failed } = await bulkTagLeads(practiceId, leadIds, selectedTagIds, tagMode);
            setTagOpen(false);
            setSelectedTagIds([]);
            const verb = tagMode === 'remove' ? 'untagged' : 'tagged';
            if (failed === 0) {
                afterAction(`${n('lead')} ${verb}`);
            } else {
                enqueueSnackbar(`${verb} — ${total - failed} of ${total} leads succeeded, ${failed} failed`, { variant: 'warning' });
                onClearSelection();
                if (onRefresh) onRefresh();
            }
        } catch {
            dispatch(updateError({ success: false, message: 'Bulk tag failed — please try again.' }));
        } finally {
            setTagLoading(false);
        }
    };

    // ── Archive ───────────────────────────────────────────────────
    const [archiveOpen, setArchiveOpen] = useState(false);
    const [archiveReason, setArchiveReason] = useState('');
    const [archiveLoading, setArchiveLoading] = useState(false);

    const handleArchiveConfirm = useCallback(() => {
        if (!archiveReason) return;
        setArchiveLoading(true);
        dispatch(
            bulkArchivedOrRestore(
                practiceId,
                { object_ids: leadIds, request_type: 'ARCHIVE', reason: archiveReason },
                (status) => {
                    setArchiveLoading(false);
                    if (status?.toLowerCase?.() === 'finish' || status === undefined) {
                        setArchiveOpen(false);
                        setArchiveReason('');
                        afterAction(`${n('lead')} archived`);
                    }
                }
            )
        );
    }, [practiceId, leadIds, archiveReason, afterAction, n]);

    // ── Render ────────────────────────────────────────────────────
    const bar = (
        <AnimatePresence>
            {count > 0 && (
                <m.div
                    key="bulk-bar"
                    initial={{ y: 40, opacity: 0, scale: 0.98 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    exit={{ y: 32, opacity: 0 }}
                    transition={{
                        default: { type: 'spring', stiffness: 380, damping: 28 },
                        exit: { duration: 0.18, ease: 'easeIn' },
                    }}
                    style={{
                        position: 'fixed',
                        bottom: 24,
                        left: 0,
                        right: 0,
                        marginLeft: 'auto',
                        marginRight: 'auto',
                        zIndex: 1400,
                        width: 'calc(100vw - 48px)',
                        maxWidth: 920,
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 2,
                            py: 1.25,
                            bgcolor: 'rgba(20, 22, 28, 0.85)',
                            backdropFilter: 'blur(20px) saturate(180%)',
                            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: 3.5,
                            boxShadow: '0 16px 48px rgba(0,0,0,0.32), 0 2px 8px rgba(0,0,0,0.16)',
                            color: '#fff',
                            minHeight: 56,
                        }}
                    >
                        {/* Count chip — pulses on count change */}
                        <m.div key={count} animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 0.2 }} style={{ flexShrink: 0 }}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                sx={{ px: 1.25, py: 0.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.10)' }}
                            >
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#fff', lineHeight: 1 }} noWrap>
                                    {count} selected
                                </Typography>
                            </Stack>
                        </m.div>

                        <Divider orientation="vertical" sx={{ height: 24, bgcolor: 'rgba(255,255,255,0.12)', mx: 0.5 }} />

                        {/* Checklist group — V2-only (Risk 3 fix).
                            On V1 practices these endpoints 403; on V2 with zero tasks
                            on selected leads they succeed-silently. Hide entirely when
                            tasks-v2 is not enabled. Reminder stays visible since it
                            isn't tied to the checklist endpoints. */}
                        {checklistActionsEnabled && (
                            <>
                                <ActionButton icon="eva:checkmark-circle-2-fill" label="Complete" onClick={() => setDoneOpen(true)} loading={doneLoading} accentColor="#36b37e" />
                                <ActionButton icon="eva:close-circle-outline" label="Skip" onClick={() => setSkipOpen(true)} loading={skipLoading} />
                                <ActionButton icon="eva:clock-outline" label="Pause" onClick={() => setPauseOpen(true)} loading={pauseLoading} />
                            </>
                        )}
                        {onOpenBulkReminder && <ActionButton icon="eva:bell-outline" label="Reminder" onClick={onOpenBulkReminder} />}
                        {checklistActionsEnabled && (
                            <ActionButton icon="eva:undo-outline" label="Reopen" onClick={() => setReopenOpen(true)} loading={reopenLoading} />
                        )}

                        <Divider orientation="vertical" sx={{ height: 24, bgcolor: 'rgba(255,255,255,0.12)', mx: 0.5 }} />

                        {/* Lead group */}
                        <ActionButton icon="eva:person-add-outline" label="Assign" onClick={() => setAssignOpen((p) => !p)} loading={assignLoading} btnRef={assignBtnRef} />
                        <ActionButton icon="eva:pricetags-outline" label="Tag" onClick={() => setTagOpen((p) => !p)} loading={tagLoading} btnRef={tagBtnRef} />
                        <ActionButton icon="eva:archive-outline" label="Archive" onClick={() => setArchiveOpen(true)} loading={archiveLoading} accentColor="#ff5630" />

                        <Divider orientation="vertical" sx={{ height: 24, bgcolor: 'rgba(255,255,255,0.12)', mx: 0.5 }} />

                        {/* Clear */}
                        <Tooltip title="Clear selection">
                            <IconButton size="small" onClick={onClearSelection} sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fff', bgcolor: BTN_HOVER_BG } }}>
                                <Iconify icon="eva:close-fill" width={16} />
                            </IconButton>
                        </Tooltip>
                    </Paper>

                    {/* Assign popover */}
                    <Popper open={assignOpen} anchorEl={assignBtnRef.current} placement="top" style={{ zIndex: 1500 }}>
                        <ClickAwayListener onClickAway={() => setAssignOpen(false)}>
                            <Paper elevation={6} sx={{ p: 2, borderRadius: 2, minWidth: 240 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Assign to</Typography>
                                <AssignedToLead assignToState={assignees} setAssignTo={setAssignees} staffRole="assignee" />
                                <Button
                                    fullWidth variant="contained" size="small"
                                    disabled={assignees.length === 0 || assignLoading}
                                    onClick={handleAssignApply}
                                    startIcon={assignLoading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
                                    sx={{ mt: 2, textTransform: 'none', borderRadius: 1.5 }}
                                >
                                    Apply to {count} lead{count !== 1 ? 's' : ''}
                                </Button>
                            </Paper>
                        </ClickAwayListener>
                    </Popper>

                    {/* Tag popover */}
                    <Popper open={tagOpen} anchorEl={tagBtnRef.current} placement="top" style={{ zIndex: 1500 }}>
                        <ClickAwayListener onClickAway={() => setTagOpen(false)}>
                            <Paper elevation={6} sx={{ p: 2, borderRadius: 2, minWidth: 260, maxWidth: 320 }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                                    <Typography variant="subtitle2">Tags</Typography>
                                    <ToggleButtonGroup
                                        size="small"
                                        exclusive
                                        value={tagMode}
                                        onChange={(_, v) => v && setTagMode(v)}
                                        sx={{ height: 28 }}
                                    >
                                        <ToggleButton value="add" sx={{ px: 1.5, fontSize: 11, textTransform: 'none' }}>Add</ToggleButton>
                                        <ToggleButton value="remove" sx={{ px: 1.5, fontSize: 11, textTransform: 'none' }}>Remove</ToggleButton>
                                    </ToggleButtonGroup>
                                </Stack>
                                <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ maxHeight: 160, overflowY: 'auto', mb: 1.5 }}>
                                    {allTags.map((tag) => {
                                        const sel = selectedTagIds.includes(tag.id);
                                        return (
                                            <Button
                                                key={tag.id}
                                                size="small"
                                                variant={sel ? 'contained' : 'outlined'}
                                                onClick={() => handleTagToggle(tag.id)}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontSize: 11,
                                                    borderRadius: 3,
                                                    py: 0.25,
                                                    px: 1,
                                                    borderColor: tag.tag_colour || 'divider',
                                                    color: sel ? '#fff' : (tag.tag_colour || 'text.primary'),
                                                    bgcolor: sel ? (tag.tag_colour || 'primary.main') : 'transparent',
                                                    '&:hover': { bgcolor: tag.tag_colour ? `${tag.tag_colour}22` : undefined },
                                                }}
                                            >
                                                {tag.name}
                                            </Button>
                                        );
                                    })}
                                    {allTags.length === 0 && (
                                        <Typography variant="caption" color="text.disabled">No tags found</Typography>
                                    )}
                                </Stack>
                                <Button
                                    fullWidth variant="contained" size="small"
                                    disabled={selectedTagIds.length === 0 || tagLoading}
                                    onClick={handleTagApply}
                                    startIcon={tagLoading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
                                    sx={{ textTransform: 'none', borderRadius: 1.5 }}
                                >
                                    {tagMode === 'remove' ? 'Remove' : 'Add'} tags — {count} lead{count !== 1 ? 's' : ''}
                                </Button>
                            </Paper>
                        </ClickAwayListener>
                    </Popper>
                </m.div>
            )}
        </AnimatePresence>
    );

    return (
        <>
            {createPortal(bar, document.body)}

            {/* Complete confirm */}
            <ConfirmDialog
                open={doneOpen}
                onClose={() => setDoneOpen(false)}
                title="Complete all checklist items?"
                body={<>Mark all checklist items as complete for <strong>{n('lead')}</strong>? This cannot be undone in bulk.</>}
                onConfirm={handleDoneConfirm}
                loading={doneLoading}
                confirmLabel="Complete"
            />

            {/* Skip confirm */}
            <ConfirmDialog
                open={skipOpen}
                onClose={() => setSkipOpen(false)}
                title="Skip all checklist items?"
                body={<>Skip all pending checklist items on <strong>{n('lead')}</strong>? Items will be marked as skipped.</>}
                onConfirm={handleSkipConfirm}
                loading={skipLoading}
                confirmLabel="Skip all"
            />

            {/* Reopen confirm */}
            <ConfirmDialog
                open={reopenOpen}
                onClose={() => setReopenOpen(false)}
                title={`Reopen checklists on ${n('lead')}?`}
                body={<>Completed and skipped items will move back to <strong>In Progress</strong>.</>}
                onConfirm={handleReopenConfirm}
                loading={reopenLoading}
                confirmLabel="Reopen"
            />

            {/* Pause dialog */}
            <Dialog open={pauseOpen} onClose={() => !pauseLoading && setPauseOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
                <DialogTitle sx={{ fontSize: '1rem', pb: 1 }}>Pause until…</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Pause all pending checklist items on <strong>{n('lead')}</strong> until:
                    </Typography>
                    <TextField
                        fullWidth
                        type="date"
                        size="small"
                        value={pauseDate}
                        onChange={(e) => setPauseDate(e.target.value)}
                        inputProps={{ min: new Date().toISOString().split('T')[0] }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => { setPauseOpen(false); setPauseDate(''); }} variant="outlined" disabled={pauseLoading} sx={{ flex: 1, borderRadius: 1.5, textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button onClick={handlePauseConfirm} variant="contained" disabled={!pauseDate || pauseLoading} sx={{ flex: 1, borderRadius: 1.5, textTransform: 'none' }}>
                        {pauseLoading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : 'Pause'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Archive confirm */}
            <Dialog open={archiveOpen} onClose={() => !archiveLoading && setArchiveOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
                <DialogTitle sx={{ fontSize: '1rem', pb: 1 }}>Archive {n('lead')}?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Select a reason:</Typography>
                    <Select fullWidth size="small" displayEmpty value={archiveReason} onChange={(e) => setArchiveReason(e.target.value)} sx={{ borderRadius: 1.5 }}>
                        <MenuItem value="" disabled>Select reason</MenuItem>
                        {ARCHIVE_REASONS.map((r) => (
                            <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                        ))}
                    </Select>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => setArchiveOpen(false)} variant="outlined" disabled={archiveLoading} sx={{ flex: 1, borderRadius: 1.5, textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleArchiveConfirm} variant="contained" color="error" disabled={!archiveReason || archiveLoading} sx={{ flex: 1, borderRadius: 1.5, textTransform: 'none' }}>
                        {archiveLoading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : 'Archive'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

FloatingBulkActionBar.propTypes = {
    practiceId: PropTypes.any.isRequired,
    onClearSelection: PropTypes.func.isRequired,
    onRefresh: PropTypes.func,
    onBulkComplete: PropTypes.func,
    onOpenBulkReminder: PropTypes.func,
    // P0: called with a skippedGroups object ({ needs_send, needs_data,
    // needs_next_action, needs_artifact } — each an array of lead-ID strings)
    // when bulk Complete skips ≥1 non-bulk-completable items.  Parent renders a
    // persistent (non-auto-dismiss) dialog with group-specific copy explaining
    // what the user must do manually for each affected lead.
    onItemsSkipped: PropTypes.func,
};

FloatingBulkActionBar.defaultProps = {
    onRefresh: null,
    onBulkComplete: null,
    onOpenBulkReminder: null,
    onItemsSkipped: null,
};

export default FloatingBulkActionBar;

// Exposed for unit tests — covers the item-level vs lead-level failure-count
// arithmetic. Each helper returns { total, failed, firstError } where `total`
// counts the underlying POSTs issued (item-level for checklist helpers,
// lead-level for tag helper).
export const __testables = {
    bulkChecklistAction,
    bulkReopenChecklist,
    bulkTagLeads,
};
